// Shrink/convert large (incl. iPhone HEIC) photos to a small JPEG in the browser
// BEFORE upload — prevents mobile memory crashes and guarantees the photo displays.
// Uses createImageBitmap (decodes + downscales efficiently, lighter on phone memory)
// with an <img>+canvas fallback for browsers that can't bitmap the file (e.g. some HEIC).
const MAX_DIM = 1400;
const QUALITY = 0.82;

const canvasToFile = (canvas, origName) =>
  new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const base = (origName || 'photo').replace(/\.[^.]+$/, '');
        const out = blob ? new File([blob], `${base}.jpg`, { type: 'image/jpeg' }) : null;
        canvas.width = 0; canvas.height = 0; // free memory
        resolve(out);
      },
      'image/jpeg',
      QUALITY
    );
  });

const viaImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve((await canvasToFile(canvas, file.name)) || file);
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

export const compressImage = async (file) => {
  if (!file || !(file.type || '').startsWith('image/')) return file;
  // Preferred: createImageBitmap decodes efficiently; we downscale + release it.
  if (typeof window !== 'undefined' && window.createImageBitmap) {
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
      if (bitmap.close) bitmap.close();
      const out = await canvasToFile(canvas, file.name);
      if (out) return out;
    } catch {
      /* fall through to <img> path */
    }
  }
  return viaImage(file);
};
